import { Maybe } from '@zaptic-external/ts-monads'
import i18next from 'i18next'
import * as React from 'react'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import type { ReportGuideDetails, ReportGuideId, SyncGuide } from '../../../shared/domain/guides/types.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { SVG_Cross } from '../svg/SVG_Cross'
import { SVG_Back } from '../svg/SVG_Back'
import { ModalBottomSheet } from '../ui/ModalBottomSheet'
import { GuideList } from './GuideList'
import { GuidePresentation } from './GuidePresentation'
import { createGuideExecution } from './helpers'
import type { AppFileGuideDetails, AppGuide, ParentInfo } from './Model'

interface Props {
    allowImageZoom: boolean
    deps: CommonDeps
    fetchAppGuides: (syncGuides: SyncGuide[], withParentInfo: boolean) => Promise<AppGuide[]>
    fetchFileGuideDetails: (fileId: FileId) => Promise<AppFileGuideDetails>
    fetchGuideParentInfo: (guide: SyncGuide) => Promise<ParentInfo[]>
    fetchReportGuideDetails: (reportGuideId: ReportGuideId) => Promise<ReportGuideDetails>
    guideToExecute?: AppGuide
    onClickExecuteGuide: (guide: AppGuide) => void
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    onBack: () => void
    onClose: () => void
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    open: boolean
    renderCachedImage: RenderCachedImage
    renderWorkflowGuideExecution: () => React.ReactElement<unknown>
    syncGuides: SyncGuide[]
    title?: string
}

export function GuideBottomSheet(props: Props) {
    const {
        allowImageZoom,
        deps,
        fetchAppGuides,
        fetchFileGuideDetails,
        fetchGuideParentInfo,
        fetchReportGuideDetails,
        guideToExecute,
        onClickExecuteGuide,
        onClickGuideParentInfo,
        onBack,
        onClose,
        onOpenFile,
        open,
        renderCachedImage,
        renderWorkflowGuideExecution,
        syncGuides,
        title,
    } = props
    const maybeGuideToExecute = Maybe.fromOptional(guideToExecute)
    const showBackButton = maybeGuideToExecute.isJust()
    const showIcon = !showBackButton

    const getDefaultTitle = () =>
        maybeGuideToExecute.map((guide) => guide.name).withDefault(i18next.t('appcore:ExecutionPage.guideModalTitle'))

    return (
        <ModalBottomSheet open={open} size="large" onClose={onClose}>
            <div className="AppCore__guide__ExecutionPage__guideModal">
                <Header
                    onBack={onBack}
                    onClose={onClose}
                    showBackButton={showBackButton}
                    showIcon={showIcon}
                    title={title || getDefaultTitle()}
                />

                <div className="AppCore__guide__ExecutionPage__guideModal-body">
                    <Body
                        deps={deps}
                        allowImageZoom={allowImageZoom}
                        fetchAppGuides={fetchAppGuides}
                        fetchFileGuideDetails={fetchFileGuideDetails}
                        fetchGuideParentInfo={fetchGuideParentInfo}
                        fetchReportGuideDetails={fetchReportGuideDetails}
                        maybeGuideToExecute={maybeGuideToExecute}
                        onClickGuideParentInfo={onClickGuideParentInfo}
                        onExecutionDone={onBack}
                        onGuideClick={onClickExecuteGuide}
                        onOpenFile={onOpenFile}
                        onOpenFileDone={onBack}
                        renderCachedImage={renderCachedImage}
                        renderWorkflowGuideExecution={renderWorkflowGuideExecution}
                        syncGuides={syncGuides}
                    />
                </div>
            </div>
        </ModalBottomSheet>
    )
}

function Header(props: {
    onBack: () => void
    onClose: () => void
    showBackButton: boolean
    showIcon: boolean
    title: string
}) {
    const { onBack, onClose, showBackButton, showIcon, title } = props
    return (
        <div className="AppCore__guide__ExecutionPage__guideModal-header">
            <div className="AppCore__guide__ExecutionPage__guideModal-header-side-item">
                {showBackButton && (
                    <button onClick={onBack}>
                        <SVG_Back />
                    </button>
                )}
            </div>
            <div className="AppCore__guide__ExecutionPage__guideModal-header-main-item">
                {showIcon && <zaptic-icon icon-name="question-mark-blue" />}
                <span>{title}</span>
            </div>
            <div className="AppCore__guide__ExecutionPage__guideModal-header-side-item">
                <button aria-label="close" onClick={onClose}>
                    <SVG_Cross />
                </button>
            </div>
        </div>
    )
}

function Body(props: {
    deps: CommonDeps
    allowImageZoom: boolean
    fetchAppGuides: (syncGuides: SyncGuide[], withParentInfo: boolean) => Promise<AppGuide[]>
    fetchFileGuideDetails: (fileId: FileId) => Promise<AppFileGuideDetails>
    fetchGuideParentInfo: (guide: SyncGuide) => Promise<ParentInfo[]>
    fetchReportGuideDetails: (reportGuideId: ReportGuideId) => Promise<ReportGuideDetails>
    maybeGuideToExecute: Maybe<AppGuide>
    onClickGuideParentInfo: (guideParent: ParentInfo) => void
    onExecutionDone: () => void
    onGuideClick: (guide: AppGuide) => void
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    onOpenFileDone: () => void
    renderCachedImage: RenderCachedImage
    renderWorkflowGuideExecution: () => React.ReactElement<unknown>
    syncGuides: SyncGuide[]
}) {
    const {
        deps,
        allowImageZoom,
        fetchAppGuides,
        fetchFileGuideDetails,
        fetchGuideParentInfo,
        fetchReportGuideDetails,
        maybeGuideToExecute,
        onClickGuideParentInfo,
        onExecutionDone,
        onGuideClick,
        onOpenFile,
        onOpenFileDone,
        renderCachedImage,
        renderWorkflowGuideExecution,
        syncGuides,
    } = props

    if (syncGuides.length === 0) {
        return <></>
    }

    const handleOnOpenFile = (downloadUrl: string, fileExtension: FileExtension, name: string) =>
        onOpenFile(downloadUrl, fileExtension, name).then(() => onOpenFileDone())

    return maybeGuideToExecute
        .map((guide) => (
            <GuidePresentation
                deps={deps}
                allowImageZoom={allowImageZoom}
                fetchGuideExecution={() =>
                    createGuideExecution(
                        guide,
                        fetchFileGuideDetails,
                        () => fetchGuideParentInfo(guide),
                        fetchReportGuideDetails
                    )
                }
                onBack={onExecutionDone}
                onClickGuideParentInfo={onClickGuideParentInfo}
                onOpenFile={handleOnOpenFile}
                renderCachedImage={renderCachedImage}
                renderWorkflowGuideExecution={renderWorkflowGuideExecution}
            />
        ))
        .withDefault(
            <GuideList
                deps={deps}
                renderCachedImage={renderCachedImage}
                fetchAppGuides={() => fetchAppGuides(syncGuides, true)}
                onClickGuideParentInfo={onClickGuideParentInfo}
                onGuideClick={onGuideClick}
                withSearch={true}
            />
        )
}
